import { z } from 'zod';
import { id, organizationId, timestamp } from './common.js';

const optionalText = (max: number) => z.string().trim().max(max).nullable();
const labels = z.array(z.string().trim().min(1).max(60)).max(12);

/** Practice-owned information shared with authenticated professionals before committing to work. */
export const practiceBriefingFields = z.object({
  practiceType: optionalText(80),
  specialties: labels,
  software: labels,
  adultAppointmentMinutes: z.number().int().min(5).max(240).nullable(),
  newPatientAppointmentMinutes: z.number().int().min(5).max(240).nullable(),
  assistanceAvailable: optionalText(600),
  equipmentProvided: optionalText(600),
  ppeRequirements: optionalText(600),
  dressCode: optionalText(300),
  parkingInstructions: optionalText(1000),
  arrivalInstructions: optionalText(1000),
  contactName: optionalText(120),
  contactPhone: z
    .string()
    .trim()
    .max(40)
    .regex(/^[+\d\s().x-]*$/, 'Enter a practice phone number')
    .nullable(),
  contactInstructions: optionalText(600),
});
export type PracticeBriefingFields = z.infer<typeof practiceBriefingFields>;

export const emptyPracticeBriefing: PracticeBriefingFields = {
  practiceType: null,
  specialties: [],
  software: [],
  adultAppointmentMinutes: null,
  newPatientAppointmentMinutes: null,
  assistanceAvailable: null,
  equipmentProvided: null,
  ppeRequirements: null,
  dressCode: null,
  parkingInstructions: null,
  arrivalInstructions: null,
  contactName: null,
  contactPhone: null,
  contactInstructions: null,
};

export const practiceProfile = practiceBriefingFields.extend({
  locationId: id,
  organizationId,
  revision: z.number().int().nonnegative(),
  updatedAt: timestamp.nullable(),
});
export type PracticeProfile = z.infer<typeof practiceProfile>;

export const updatePracticeProfileInput = practiceBriefingFields
  .partial()
  .extend({
    expectedRevision: z.number().int().nonnegative(),
  })
  .strict()
  .refine((input) => Object.keys(input).length > 1, 'Provide at least one briefing field');
export type UpdatePracticeProfileInput = z.infer<typeof updatePracticeProfileInput>;
